import moment from 'moment';
import { FunctionComponent, JSX, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { TransferFundRequest } from '../types';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import {
  Table,
  Box,
  Title,
  SubTitle,
  Button,
  Badge,
  Description,
  TransactionsLists,
  TableProps,
  capitalizeFirstLetter,
  GetColorStatus,
  formattedCurrency,
} from 'vopay';

type FC = FunctionComponent;

type NewType = JSX.Element;

type KeyValuePair = {
  display: string;
  value: string | NewType;
  visible?: boolean;
};

type ID = {
  ClientAccountID: string;
  ID: string;
};

type DataProps = {
  FundingTransactionID: string;
  WithdrawTransactionID: string;
  WithdrawTransactionIDs: Record<string, ID>;
  AccountTransferID: string;
  AccountTransferIDs: Record<string, ID>;
  DebitorClientAccount: string;
  RecipientClientAccount: string;
  Country: string;
  values: TransferFundRequest;
};

const FundWithdrawTransferDetails: FC = () => {
  const history = useHistory();
  const { onSubNavClick } = useNavMenu();
  const { setOpenModule } = useOpenModule();
  const { state } = useLocation<DataProps>();
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const [transferIds, setTransferIds] = useState<string[]>([]);
  const [withdrawIds, setWithdrawIds] = useState<string[]>([]);

  useEffect(() => {
    if (
      state.FundingTransactionID &&
      (state.AccountTransferID || state.AccountTransferIDs) &&
      (state.WithdrawTransactionID || state.WithdrawTransactionIDs)
    ) {
      const transactionIDs = [state.FundingTransactionID];

      if (state.AccountTransferID !== '') {
        transactionIDs.push(state.AccountTransferID);
        setTransferIds([state.AccountTransferID]);
      } else if (Object.keys(state.AccountTransferIDs).length !== 0) {
        const ids = Object.values(state.AccountTransferIDs).map((item: ID) => item.ID);
        setTransferIds(ids);
        transactionIDs.push(...ids);
      }

      if (state.WithdrawTransactionID !== '') {
        transactionIDs.push(state.WithdrawTransactionID);
        setWithdrawIds([state.WithdrawTransactionID]);
      } else if (Object.keys(state.WithdrawTransactionIDs).length !== 0) {
        const ids = Object.values(state.WithdrawTransactionIDs).map((item: ID) => item.ID);
        setWithdrawIds(ids);
        transactionIDs.push(...ids);
      }

      fetchTransactionsHistory({
        StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
        EndDateTime: moment().utc().format('YYYY-MM-DD'),
        TransactionID: transactionIDs.join(','),
      });
    }
  }, []);

  return (
    <>
      <Box mb="md">
        <Title title="Fund, Transfer and Withdraw Overview" />
      </Box>

      <Box pt="md" pb="md">
        <SubTitle subTitle="Fund" />
      </Box>
      {transactions
        .filter((subTransaction: TransactionsLists) => subTransaction.TransactionID === state.FundingTransactionID)
        .map((subTransaction: TransactionsLists) => (
          <Box key={subTransaction.TransactionID} mt="md" flx="col">
            <Description bold>Transaction Details</Description>
            <Table {...createFundDetails(subTransaction)} />
          </Box>
        ))}

      <Box pt="md" pb="md">
        <SubTitle subTitle="Transfer" />
      </Box>
      {transactions
        .filter((subTransaction: TransactionsLists) => transferIds.includes(subTransaction.TransactionID))
        .map((subTransaction: TransactionsLists) => (
          <Box key={subTransaction.TransactionID} mt="md" flx="col">
            <Description bold>Transaction Details - {getRecipientName(subTransaction.TransactionID)}</Description>
            <Table {...createTransferDetails(subTransaction)} />
          </Box>
        ))}

      <Box pt="md" pb="md">
        <SubTitle subTitle="Withdraw" />
      </Box>
      {transactions
        .filter((subTransaction: TransactionsLists) => withdrawIds.includes(subTransaction.TransactionID))
        .map((subTransaction: TransactionsLists) => (
          <Box key={subTransaction.TransactionID} mt="md" flx="col">
            <Description bold>
              Transaction Details - {getWithdrawRecipientName(subTransaction.TransactionID)}
            </Description>
            <Table {...createWithdrawDetails(subTransaction)} />
          </Box>
        ))}
    </>
  );

  function createFundDetails(subTransaction: TransactionsLists): TableProps {
    const transactionDetailsPairs: KeyValuePair[] = [
      {
        display: 'Transaction ID',
        value: (
          <Box gap="md" ai="center">
            {subTransaction.TransactionID}
            <Button
              id="fund-transaction-id"
              label="Details"
              color="secondary"
              size="sm"
              onClick={() => {
                setOpenModule('statements');
                onSubNavClick('transactionsHistory', '/transactions-history', 'Transactions History');
                history.push({
                  pathname: `/transactions-history/details/EFTFunding/${subTransaction.TransactionID}/${subTransaction.Currency}`,
                  state: { transactionId: subTransaction.TransactionID },
                });
              }}
            />
          </Box>
        ),
      },
      {
        display: 'Transaction Status',
        value: <Badge label={capitalizeFirstLetter('Pending')} hasIcon color={GetColorStatus('pending')} />,
      },
      {
        display: 'Transaction Date',
        value: moment(subTransaction.TransactionDateTime).format('YYYY-MM-DD'),
      },
      {
        display: 'Last Modified',
        value: moment(subTransaction.LastModified).format('YYYY-MM-DD'),
      },
      {
        display: 'Type',
        value: 'Fund',
      },
      {
        display: 'Amount',
        value: `$${formattedCurrency(subTransaction.CreditAmount)}`,
      },
      {
        display: 'Currency',
        value: `${subTransaction.Currency}`,
      },
    ];

    const transactionDetailsProps: TableProps = {
      name: 'transaction-details',
      variant: 'pair',
      columns: {
        detailKey: { header: 'Transaction Detail' },
        detailValue: { header: '' },
      },
      rows: transactionDetailsPairs.map(({ display, value }) => ({
        detailKey: display,
        detailValue: value,
      })),
    };

    return transactionDetailsProps;
  }

  function createTransferDetails(subTransaction: TransactionsLists): TableProps {
    const transactionDetailsPairs: KeyValuePair[] = [
      {
        display: 'Transaction ID',
        value: (
          <Box gap="md" ai="center">
            {subTransaction.TransactionID}
            <Button
              id="fund-transaction-id"
              label="Details"
              color="secondary"
              size="sm"
              onClick={() => {
                setOpenModule('statements');
                onSubNavClick('transactionsHistory', '/transactions-history', 'Transactions History');
                history.push({
                  pathname: `/transactions-history/details/AccountTransfer/${subTransaction.TransactionID}/${subTransaction.Currency}`,
                  state: { transactionId: subTransaction.TransactionID },
                });
              }}
            />
          </Box>
        ),
      },
      {
        display: 'Type',
        value: 'Account Transfer',
      },
      {
        display: 'Debitor Client Account',
        value: state.DebitorClientAccount,
      },
      {
        display: 'Recipient Client Account',
        value: getRecipientName(subTransaction.TransactionID),
      },
      {
        display: 'Amount',
        value: `$${formattedCurrency(subTransaction.DebitAmount)}`,
      },
      {
        display: 'Currency',
        value: `${subTransaction.Currency}`,
      },
    ];

    const transactionDetailsProps: TableProps = {
      name: 'transaction-details',
      variant: 'pair',
      columns: {
        detailKey: { header: 'Transaction Detail' },
        detailValue: { header: '' },
      },
      rows: transactionDetailsPairs.map(({ display, value }) => ({
        detailKey: display,
        detailValue: value,
      })),
    };

    return transactionDetailsProps;
  }

  function getWithdrawRecipientName(transactionID: string): string {
    const result = transactions.find(
      (subTransaction: TransactionsLists) => subTransaction.TransactionID === transactionID,
    );
    return result ? result.FullName : 'Recipient client Name Not Found';
  }

  function getRecipientName(transactionID: string): string {
    const result = transactions.find(
      (subTransaction: TransactionsLists) => subTransaction.ParentTransactionID === transactionID,
    );
    return result ? result.FullName : 'Recipient client Name Not Found';
  }

  function createWithdrawDetails(subTransaction: TransactionsLists): TableProps {
    const transactionDetailsPairs: KeyValuePair[] = [
      {
        display: 'Transaction ID',
        value: (
          <Box gap="md" ai="center">
            {subTransaction.TransactionID}
            <Button
              id="fund-transaction-id"
              label="Details"
              color="secondary"
              onClick={() => {
                setOpenModule('statements');
                onSubNavClick('transactionsHistory', '/transactions-history', 'Transactions History');
                history.push({
                  pathname: `/transactions-history/details/EFTWithdraw/${subTransaction.TransactionID}/${subTransaction.Currency}`,
                  state: { transactionId: subTransaction.TransactionID },
                });
              }}
            />
          </Box>
        ),
      },
      {
        display: 'Transaction Status',
        value: <Badge label={capitalizeFirstLetter('Pending')} hasIcon color={GetColorStatus('pending')} />,
      },
      {
        display: 'Transaction Date',
        value: moment(subTransaction.TransactionDateTime).format('YYYY-MM-DD'),
      },
      {
        display: 'Last Modified',
        value: moment(subTransaction.LastModified).format('YYYY-MM-DD'),
      },
      {
        display: 'Type',
        value: 'Withdraw',
      },
      {
        display: 'Amount',
        value: `$${formattedCurrency(subTransaction.DebitAmount)}`,
      },
      {
        display: 'Currency',
        value: `${subTransaction.Currency}`,
      },
    ];

    const transactionDetailsProps: TableProps = {
      name: 'transaction-details',
      variant: 'pair',
      columns: {
        detailKey: { header: 'Transaction Detail' },
        detailValue: { header: '' },
      },
      rows: transactionDetailsPairs.map(({ display, value }) => ({
        detailKey: display,
        detailValue: value,
      })),
    };

    return transactionDetailsProps;
  }
};

export default FundWithdrawTransferDetails;
